import axios, { AxiosError, type InternalAxiosRequestConfig } from "axios";
import { isRefreshInFlight, readRefreshedAccessToken, runExclusiveRefresh } from "../refresh_lock";
import tokenService from "../token_service";

const DEBUG = import.meta.env.NODE_ENV === "dev";

const log = {
  info: (label: string, data?: any) => DEBUG && console.log(`ℹ️ [${label}]`, data),
  success: (label: string, data?: any) => DEBUG && console.log(`✅ [${label}]`, data),
  warn: (label: string, data?: any) => console.warn(`⚠️ [${label}]`, data),
  error: (label: string, data?: any) => console.error(`❌ [${label}]`, data),
};

const api = axios.create({
  baseURL: import.meta.env.VITE_APP_NEW_BACKEND_ENDPOINT,
  timeout: 30000,
  withCredentials: true,
});

// Dedicated client for token refresh (avoids infinite recursion)
const refreshClient = axios.create({
  baseURL: import.meta.env.VITE_APP_NEW_BACKEND_ENDPOINT,
  timeout: 10000,
  withCredentials: true,
});

// ========================================
// CONTROLE DE AUTO-REFRESH
// ========================================
let autoRefreshInterval: NodeJS.Timeout | null = null;
const AUTO_REFRESH_INTERVAL = 15 * 60 * 1000; // 15 minutos

// ========================================
// HELPERS
// ========================================
const isAuthEndpoint = (url: string = ""): boolean => {
  return url.includes("/auth/") || url.includes("/password/");
};

const isPublicEndpoint = (url: string = ""): boolean => {
  const publicRoutes = [
    "/auth/login",
    "/auth/validate",
    "/auth/send-code",
    "/auth/create-patient",
    "/auth/validate-identifier",
    "/password/reset",
  ];
  return publicRoutes.some((route) => url.includes(route));
};

// ========================================
// EVENTOS CUSTOMIZADOS
// ========================================
const notifyTokenExpired = () => {
  log.warn("EVENT_EMIT", "token:expired");
  window.dispatchEvent(new Event("token:expired"));
};

const notifySessionRevoked = () => {
  log.warn("EVENT_EMIT", "session:revoked");
  window.dispatchEvent(new Event("session:revoked"));
};

// ========================================
// REFRESH FUNCTION (SINGLE SOURCE OF TRUTH)
// ========================================
const performRefresh = async (): Promise<string> => {
  // A refresh already in flight, from this module or the other API module,
  // is shared instead of starting a second rotation.
  if (isRefreshInFlight()) {
    log.info("REFRESH_WAITING", "Aguardando refresh em andamento");
  }

  return runExclusiveRefresh(async () => {
    log.info("REFRESH_START", "Iniciando refresh do token");

    try {
      const response = await refreshClient.post(
        "/auth/refresh",
        {},
        {
          withCredentials: true,
          timeout: 10000,
        }
      );

      const newToken = readRefreshedAccessToken(response.data);
      const { expiresAt } = response.data;

      if (!newToken) {
        throw new Error("Refresh retornou token inválido");
      }

      // Update token in service
      tokenService.setTokens(newToken, expiresAt);

      // Update default axios header
      api.defaults.headers.common.Authorization = `Bearer ${newToken}`;

      log.success("REFRESH_SUCCESS", {
        expiresIn: tokenService.getTokenTimeLeft(),
      });

      return newToken;
    } catch (error: any) {
      log.error("REFRESH_FAILED", {
        status: error.response?.status,
        message: error.message,
      });

      // Se o refresh falhar com 401/403, limpa tudo e notifica
      if (error.response?.status === 401 || error.response?.status === 403) {
        tokenService.clearTokens();
        stopAutoRefresh();
        notifyTokenExpired();
      }

      throw error;
    }
  });
};

// ========================================
// AUTO-REFRESH
// ========================================
export const startAutoRefresh = () => {
  // Limpa intervalo existente
  if (autoRefreshInterval) {
    clearInterval(autoRefreshInterval);
  }

  log.success("AUTO_REFRESH_START", `Intervalo: ${AUTO_REFRESH_INTERVAL / 60000}min`);

  autoRefreshInterval = setInterval(async () => {
    try {
      // Check for valid token
      if (!tokenService.hasValidToken()) {
        log.info("AUTO_REFRESH_SKIP", "Token inválido ou ausente");
        stopAutoRefresh();
        return;
      }

      // Check if token is about to expire (5 minutes)
      if (tokenService.isTokenExpiringSoon(5 * 60)) {
        log.info("AUTO_REFRESH_TRIGGER", "Token próximo de expirar");
        await performRefresh();
      }
    } catch (error) {
      log.error("AUTO_REFRESH_ERROR", error);
    }
  }, AUTO_REFRESH_INTERVAL);
};

export const stopAutoRefresh = () => {
  if (autoRefreshInterval) {
    clearInterval(autoRefreshInterval);
    autoRefreshInterval = null;
    log.warn("AUTO_REFRESH_STOP", "Intervalo parado");
  }
};

// ========================================
// INTERCEPTOR DE REQUEST
// ========================================
api.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const url = config.url || "";

    // Public endpoints don't require a token
    if (isPublicEndpoint(url)) {
      return config;
    }

    const token = tokenService.getAccessToken();

    // No token — let through; backend will reject with 401
    if (!token) {
      return config;
    }

    // Adiciona token ao header
    if (config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Token expiring within 2 minutes — preemptive refresh
    if (tokenService.isTokenExpiringSoon(2 * 60) && !isRefreshInFlight()) {
      log.warn("REQUEST_PREVENTIVE_REFRESH", "Token expirando em menos de 2min");

      try {
        const newToken = await performRefresh();
        if (config.headers) {
          config.headers.Authorization = `Bearer ${newToken}`;
        }
      } catch (error) {
        log.error("PREVENTIVE_REFRESH_ERROR", error);
        // Continue with old token; backend will reject if needed
      }
    }

    return config;
  },
  (error) => {
    log.error("REQUEST_INTERCEPTOR_ERROR", error.message);
    return Promise.reject(error);
  }
);

// ========================================
// INTERCEPTOR DE RESPONSE
// ========================================
api.interceptors.response.use(
  (response) => {
    // Update token if response carries a new one
    if (response.data?.token && response.data?.expiresAt) {
      log.info("RESPONSE_TOKEN_UPDATE", "Novo token recebido");
      tokenService.setTokens(response.data.token, response.data.expiresAt);

      // Ensure auto-refresh is active
      if (!autoRefreshInterval) {
        startAutoRefresh();
      }
    }

    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    const url = originalRequest?.url || "";

    log.error("RESPONSE_ERROR", {
      url,
      status: error.response?.status,
      message: (error.response?.data as any)?.message,
    });

    // ========================================
    // TRATAMENTO DE 401 UNAUTHORIZED
    // ========================================
    if (error.response?.status === 401) {
      // Already retried — give up
      if (originalRequest._retry) {
        log.error("401_FINAL_REJECT", "Já fez retry, deslogando");
        tokenService.clearTokens();
        stopAutoRefresh();
        notifyTokenExpired();
        return Promise.reject(error);
      }

      // Don't attempt refresh on auth endpoints
      if (isAuthEndpoint(url) || url.includes("/auth/refresh")) {
        log.warn("401_SKIP_REFRESH", "Endpoint de autenticação");
        return Promise.reject(error);
      }

      // Mark as retried
      originalRequest._retry = true;

      try {
        // Attempt refresh (reuse shared promise if already running)
        const newToken = await performRefresh();

        // Update header on the original request
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
        }

        // Retry the original request
        log.info("401_RETRY", "Tentando requisição novamente");
        return api(originalRequest);
      } catch (refreshError) {
        // Se o refresh falhar, limpa tudo e notifica
        log.error("401_REFRESH_FAILED", refreshError);
        tokenService.clearTokens();
        stopAutoRefresh();
        notifyTokenExpired();
        return Promise.reject(error);
      }
    }

    // ========================================
    // TRATAMENTO DE 403 FORBIDDEN
    // ========================================
    if (error.response?.status === 403) {
      const message = (error.response.data as any)?.message || "";

      // Session revoked or fingerprint mismatch
      if (
        message.includes("Device fingerprint mismatch") ||
        message.includes("Sessão inválida") ||
        message.includes("sessão foi encerrada")
      ) {
        log.error("403_SESSION_REVOKED", message);
        tokenService.clearTokens();
        stopAutoRefresh();
        notifySessionRevoked();
      }
    }

    return Promise.reject(error);
  }
);

export default api;
export { notifyTokenExpired, notifySessionRevoked };